import React, { createContext, useMemo, useState } from "react";
import * as yup from "yup";

import { Checklist, QuestionLogics, Questions } from "Models/checklist";
import { UseState } from "Types";
import { NumberLogicOption, Status, TextLogicOption } from "Util/enums";
import {
  getLogicConditionInitialValue,
  handleQuestionOptions,
  QuestionTypes,
  updateLogicConditions,
} from "../../utils";

interface UpdateQuestionParams {
  value: any;
  field: keyof Questions;
  temporaryId?: string;
  questionId?: number;
}

interface UpdateQuestionLogicsParams {
  value: any;
  field: keyof QuestionLogics;
  logicTemporaryId?: string;
  questionLogicId?: number;
}

interface UpdateMultipleQuestionLogicsParams {
  fields: Partial<QuestionLogics>;
  logicTemporaryId?: string;
  questionLogicId?: number;
}

interface RemoveQuestionParams {
  questionId?: number;
  temporaryId?: string;
}

interface RemoveQuestionLogicParams {
  logicId?: number;
  temporaryId?: string;
}

interface ReorderQuestionsParams {
  sourceQuestionId?: number;
  sourceTemporaryId?: string;
  targetQuestionId?: number;
  targetTemporaryId?: string;
  parentPath: string | number;
}

export const MAXIMUM_LOGICS_PER_QUESTION = 3;
export const MAXIMUM_QUESTIONS_PER_LOGIC = 3;
export const MAXIMUM_QUESTIONS_DEPTH = 3;

interface ChecklistContextProps {
  checklist?: Checklist;
  setChecklist: UseState<Checklist | undefined>;
  errors?: yup.ValidationError[];
  setErrors: UseState<yup.ValidationError[]>;
  handleClearFields: () => void;
  handleUpdateChecklist: (checklistToUpdate?: Checklist) => void;
  handleRemoveQuestion: (params: RemoveQuestionParams) => void;
  handleUpdateQuestion: (params: UpdateQuestionParams) => void;
  handleUpdateMultipleFieldsInQuestionLogic: (params: UpdateMultipleQuestionLogicsParams) => void;
  handleRemoveQuestionLogics: (params: RemoveQuestionLogicParams) => void;
  handleUpdateQuestionLogics: (params: UpdateQuestionLogicsParams) => void;
  handleReorderQuestions?: (params: ReorderQuestionsParams) => void;
}

const ChecklistContext = createContext<ChecklistContextProps>({} as ChecklistContextProps);

interface ChecklistProviderProps {
  children: React.ReactChild;
}

const ChecklistProvider = ({ children }: ChecklistProviderProps) => {
  const [checklist, setChecklist] = useState<Checklist>();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const emptyChecklist = useMemo(
    () => ({
      code: "",
      description: "",
      type: "",
      questions: [
        {
          answerAction: "",
          description: "",
          mandatory: true,
          needAction: false,
          questionOrder: 1,
          status: Status.ACTIVE,
          title: "",
          type: QuestionTypes.TEXT,
          temporaryId: Math.random().toString(36).substring(2, 15),
          questionLogics: [],
        },
      ],
      appEvents: [
        {
          type: "",
        },
      ],
      notes: "",
      status: Status.ACTIVE,
      signature: false,
      jobsType: [],
      tasksType: [],
    }),
    []
  );

  const handleUpdateChecklist = (checklistToUpdate?: Checklist) => {
    if (checklistToUpdate) {
      setChecklist((state) =>
        state ? { ...state, ...checklistToUpdate } : { ...checklistToUpdate }
      );

      return;
    }
    setChecklist({
      ...emptyChecklist,
      questions: [...emptyChecklist.questions],
    });
  };

  const handleClearFields = () => setChecklist({ ...emptyChecklist });

  const handleUpdateQuestion = ({
    value,
    field,
    temporaryId,
    questionId,
  }: UpdateQuestionParams) => {
    if (!checklist) return;

    const updateQuestionsRecursively = (
      questions?: Checklist["questions"]
    ): Checklist["questions"] => {
      if (!questions) return;

      return questions?.map((question) => {
        if (
          (questionId && question.id === questionId) ||
          (temporaryId && question.temporaryId === temporaryId)
        ) {
          // updates question logic initial condition value depending on question type
          if (field === "type") {
            const logicNewCondition = getLogicConditionInitialValue({
              questionType: value,
              isRequired: question.mandatory,
            });

            return {
              ...question,
              [field]: value,
              questionLogics: updateLogicConditions({
                newCondition: logicNewCondition,
                logics: question.questionLogics,
                questionType: value,
              }),
              options: handleQuestionOptions(value, question.options),
            };
          }

          // updates question logic initial condition value depending on question required state
          if (field === "mandatory") {
            const logicNewCondition = getLogicConditionInitialValue({
              questionType: question.type,
              isRequired: value,
            });

            return {
              ...question,
              [field]: value,
              questionLogics: updateLogicConditions({
                newCondition: logicNewCondition,
                logics: question.questionLogics,
                questionType: question.type,
              }),
            };
          }

          return { ...question, [field]: value };
        }

        if (question.questionLogics && question.questionLogics.length > 0) {
          const updatedLogics = question.questionLogics.map((logic) => {
            const updatedQuestionsInLogic = updateQuestionsRecursively(logic.questions || []);
            return { ...logic, questions: updatedQuestionsInLogic };
          });
          return { ...question, questionLogics: updatedLogics };
        }

        return question;
      });
    };

    const updatedQuestions = updateQuestionsRecursively(checklist.questions || []);

    setChecklist((state) =>
      state ? { ...state, questions: updatedQuestions } : { questions: updatedQuestions }
    );
  };

  const handleUpdateQuestionLogics = ({
    value,
    field,
    logicTemporaryId,
    questionLogicId,
  }: UpdateQuestionLogicsParams) => {
    if (!checklist) return;

    // Recursive function to update questionLogics at any depth
    const updateLogicsRecursively = (
      questions?: Checklist["questions"]
    ): Checklist["questions"] => {
      if (!questions) return;

      return questions.map((question) => {
        let updatedQuestion = { ...question };

        if (question.questionLogics && question.questionLogics.length > 0) {
          const updatedLogics = question.questionLogics.map((logic) => {
            // If this is the logic to update, update the field
            if (
              (logicTemporaryId && logic.temporaryId === logicTemporaryId) ||
              (questionLogicId && logic.id === questionLogicId)
            ) {
              // handling logic value for blank
              if (field === "logicCondition") {
                if (
                  value === TextLogicOption.IS ||
                  value === TextLogicOption.IS_NOT ||
                  value === NumberLogicOption.IS ||
                  value === NumberLogicOption.IS_NOT
                ) {
                  return { ...logic, [field]: value, logicValue: "blank" };
                } else {
                  return { ...logic, [field]: value, logicValue: "" };
                }
              }

              return { ...logic, [field]: value };
            }
            // Otherwise, recursively update logics inside nested questions
            if (logic.questions && logic.questions.length > 0) {
              return {
                ...logic,
                questions: updateLogicsRecursively(logic.questions),
              };
            }
            return logic;
          });
          updatedQuestion.questionLogics = updatedLogics;
        }

        return updatedQuestion;
      });
    };

    const updatedQuestions = updateLogicsRecursively(checklist.questions || []);

    setChecklist((state) =>
      state ? { ...state, questions: updatedQuestions } : { questions: updatedQuestions }
    );
  };

  const handleUpdateMultipleFieldsInQuestionLogic = ({
    fields,
    logicTemporaryId,
    questionLogicId,
  }: UpdateMultipleQuestionLogicsParams) => {
    if (!checklist) return;

    const updateLogicsRecursively = (
      questions?: Checklist["questions"]
    ): Checklist["questions"] => {
      if (!questions) return;

      return questions.map((question) => {
        let updatedQuestion = { ...question };

        if (question.questionLogics && question.questionLogics.length > 0) {
          const updatedLogics = question.questionLogics.map((logic) => {
            if (
              (logicTemporaryId && logic.temporaryId === logicTemporaryId) ||
              (questionLogicId && logic.id === questionLogicId)
            ) {
              return { ...logic, ...fields };
            }
            if (logic.questions && logic.questions.length > 0) {
              return {
                ...logic,
                questions: updateLogicsRecursively(logic.questions),
              };
            }
            return logic;
          });
          updatedQuestion.questionLogics = updatedLogics;
        }

        return updatedQuestion;
      });
    };

    const updatedQuestions = updateLogicsRecursively(checklist.questions || []);

    setChecklist((state) =>
      state ? { ...state, questions: updatedQuestions } : { questions: updatedQuestions }
    );
  };

  const handleRemoveQuestion = ({ questionId, temporaryId }: RemoveQuestionParams) => {
    if (!checklist) return;

    const removeQuestionsRecursively = (
      questions?: Checklist["questions"]
    ): Checklist["questions"] => {
      if (!questions) return;

      // Remove the question if it matches the id or temporaryId
      const filtered = questions
        .filter(
          (question) =>
            !(
              (questionId && question.id === questionId) ||
              (temporaryId && question.temporaryId === temporaryId)
            )
        )
        .map((question) => {
          // Recursively remove from nested logics
          if (question.questionLogics && question.questionLogics.length > 0) {
            const updatedLogics = question.questionLogics.map((logic) => ({
              ...logic,
              questions: removeQuestionsRecursively(logic.questions),
            }));
            return { ...question, questionLogics: updatedLogics };
          }
          return question;
        });

      return filtered.map((question, index) => ({
        ...question,
        questionOrder: index + 1,
      }));
    };

    const updatedQuestions = removeQuestionsRecursively(checklist.questions || []);

    setChecklist((state) =>
      state ? { ...state, questions: updatedQuestions } : { questions: updatedQuestions }
    );
  };

  const handleRemoveQuestionLogics = ({ logicId, temporaryId }: RemoveQuestionLogicParams) => {
    if (!checklist) return;

    const removeLogicsRecursively = (
      questions?: Checklist["questions"]
    ): Checklist["questions"] => {
      if (!questions) return;

      return questions.map((question) => {
        let updatedQuestion = { ...question };

        if (question.questionLogics && question.questionLogics.length > 0) {
          // Filter out the logic that matches the id or temporaryId
          const filteredLogics = question.questionLogics
            .filter(
              (logic) =>
                !(
                  (logicId && logic.id === logicId) ||
                  (temporaryId && logic.temporaryId === temporaryId)
                )
            )
            .map((logic) => {
              // Recursively remove from nested questions within remaining logics
              if (logic.questions && logic.questions.length > 0) {
                return {
                  ...logic,
                  questions: removeLogicsRecursively(logic.questions),
                };
              }
              return logic;
            });

          updatedQuestion.questionLogics = filteredLogics;
        }

        return updatedQuestion;
      });
    };

    const updatedQuestions = removeLogicsRecursively(checklist.questions || []);

    setChecklist((state) =>
      state ? { ...state, questions: updatedQuestions } : { questions: updatedQuestions }
    );
  };

  const handleReorderQuestions = ({
    sourceQuestionId,
    sourceTemporaryId,
    targetQuestionId,
    targetTemporaryId,
    parentPath,
  }: ReorderQuestionsParams) => {
    if (!checklist) return;

    const findQuestionIndex = (
      questions: Questions[],
      id?: number,
      temporaryId?: string
    ): number => {
      return questions.findIndex(
        (question) => (id && question.id === id) || (temporaryId && question.temporaryId === temporaryId)
      );
    };

    const reorderAtLevel = (questions: Questions[]): Questions[] => {
      const sourceIndex = findQuestionIndex(questions, sourceQuestionId, sourceTemporaryId);
      const targetIndex = findQuestionIndex(questions, targetQuestionId, targetTemporaryId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return questions;
      }

      const newQuestions = [...questions];
      const [reorderedQuestion] = newQuestions.splice(sourceIndex, 1);
      newQuestions.splice(targetIndex, 0, reorderedQuestion);

      return newQuestions.map((question, index) => ({
        ...question,
        questionOrder: index + 1,
      }));
    };

    const processNestedLogics = (question: Questions): Questions => {
      if (!question.questionLogics) return question;

      return {
        ...question,
        questionLogics: question.questionLogics.map((logic) => ({
          ...logic,
          questions: reorderRecursively(logic.questions),
        })),
      };
    };

     const isTargetLogic = (logic: QuestionLogics): boolean => {
      const logicIdentifier = logic.id || logic.temporaryId;
      return logicIdentifier === parentPath;
    };

    const reorderRecursively = (questions?: Questions[]): Questions[] => {
      if (!questions) return [];

      if (parentPath === "root") {
        return reorderAtLevel(questions).map(processNestedLogics);
      }

      return questions.map((question) => {
        if (!question.questionLogics) return question;

        const updatedLogics = question.questionLogics.map((logic) => {
          // found target logic, reorder its questions
          if (isTargetLogic(logic) && logic.questions) {
            return { ...logic, questions: reorderAtLevel(logic.questions) };
          }

          // not target, continue searching deeper
          return { ...logic, questions: reorderRecursively(logic.questions) };
        });

        return { ...question, questionLogics: updatedLogics };
      });
    };

    const updatedQuestions = reorderRecursively(checklist.questions || []);

    setChecklist((state) =>
      state ? { ...state, questions: updatedQuestions } : { questions: updatedQuestions }
    );
  };

  return (
    <ChecklistContext.Provider
      value={{
        checklist,
        errors,
        setErrors,
        setChecklist,
        handleClearFields,
        handleUpdateChecklist,
        handleRemoveQuestion,
        handleUpdateQuestion,
        handleUpdateMultipleFieldsInQuestionLogic,
        handleRemoveQuestionLogics,
        handleUpdateQuestionLogics,
        handleReorderQuestions,
      }}
    >
      {children}
    </ChecklistContext.Provider>
  );
};

export { ChecklistContext, ChecklistProvider };
